/**
 * ชนิดข้อมูลของ Packing Unit (P2-9b, D-105) — เขียนตาม Pydantic schema จริงที่
 * เซิร์ฟเวอร์ serve (`apps/api/src/cap_erp_api/modules/sales/schemas.py`,
 * `.../packing.py`) ไม่ใช่ตาม `docs/API_CONTRACT.md` เพียงอย่างเดียว
 *
 * ⚠️ **`docs/API_CONTRACT.md` หัวข้อ "Packing & Delivery" เอ่ยถึง
 * `POST /v1/packing-units/{id}/lots` แต่ router จริง (`modules/sales/router.py`)
 * ไม่ได้ลงทะเบียน route นั้นเลย** — ทั้งไฟล์ module docstring ของ router
 * ("P2-9b, the package (D-105 items 1-3): POST/GET on /v1/packing-units, GET on
 * one record, and confirm") และ `schemas.py` (มีแค่ `PackingUnitLotRequest` ที่
 * เป็นสมาชิกของ `lots` array ใน `PackingUnitCreateRequest`, ไม่มี request schema
 * แยกสำหรับ "add lot") ยืนยันตรงกันว่า **lot ทั้งหมดของกล่องต้องมากับคำขอสร้าง
 * ครั้งเดียว** (`POST /v1/packing-units` รับทั้งหัวกล่องและ `lots` ในคำขอเดียว)
 * ไม่มีเส้นทางใดที่ "เพิ่ม lot เข้ากล่องที่เปิดอยู่แล้ว" — นี่คือความขัดแย้งระหว่าง
 * เอกสารกับ backend จริงที่ AGENTS.md ข้อ "ถ้าเอกสารขัดกัน...ห้าม Agent ย่อยเลือก
 * คำตอบเอง" พูดถึง จึงถูกรายงานเป็น blocker ใน handoff — ไฟล์นี้และ `client.ts`
 * สร้างเฉพาะ route ที่ backend serve จริงเท่านั้น (invariant ข้อแรกของ WO นี้)
 *
 * ชนิดข้อมูลข้างล่างบังคับตาม field ที่ Pydantic ประกาศไว้ตรง ๆ เพราะมีซอร์สของ
 * เซิร์ฟเวอร์ให้อ่านจริง (ต่างจาก `lib/sales/types.ts` ที่ยอมให้ field ประกอบเป็น
 * nullable เพราะ contract เอกสารไม่ได้เขียนตัวอย่าง response ไว้ทีละฟิลด์)
 */

/** รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — เดียวกับใบสั่งขาย (`SALES_MODULE` ใน
 * `modules/sales/schemas.py`) เพราะ Packing เป็นงานปลายทางการส่งของแผนกขาย
 * ประกาศแยกไว้ที่นี่แทนการ import จาก `lib/sales/types.ts` ด้วยเหตุผลเดียวกับที่
 * `lib/sales/client.ts` ไม่ import จาก `lib/procurement/client.ts`: เป็นคนละไฟล์
 * ที่ Work Order คนละใบเป็นเจ้าของ (`lib/sales/**` อยู่ใน forbidden_scope ของ
 * `WO-P2-M9-006`) ผูกกันด้วยค่าคงที่ที่บังเอิญเท่ากัน ไม่ใช่ import เดียวกัน */
export const PACKING_MODULE = 'SALES';

/* ------------------------------------------------------------- สถานะ ----- */

export const DRAFT_PACKING_STATUS = 'DRAFT';
export const PACKING_PACKING_STATUS = 'PACKING';
export const PACKED_PACKING_STATUS = 'PACKED';
export const LOADED_PACKING_STATUS = 'LOADED';
export const DISPATCHED_PACKING_STATUS = 'DISPATCHED';
export const RETURNED_PACKING_STATUS = 'RETURNED';

/**
 * หกสถานะของ `packing_unit.status` (`modules/sales/packing.py`,
 * `PackingUnitStatus`) — `DRAFT` → `PACKING` → `PACKED` → `LOADED` →
 * `DISPATCHED` และ `RETURNED` นอกเส้นทาง
 *
 * หน้าจอนี้ (`WO-P2-M9-006`) เขียนได้แค่สองทาง: `POST /packing-units` เปิดกล่อง
 * ที่ `PACKING` เสมอ (ไม่มีใครสร้าง `DRAFT`) และ `confirm`/`unpack` สลับระหว่าง
 * `PACKING` ↔ `PACKED` `LOADED`/`DISPATCHED` เป็นของงาน Delivery
 * (`WO-P2-M9-007`, แยก Work Order) และ `RETURNED` เป็นของการกลับรายการ Delivery
 * — หน้าจอนี้ต้อง**อ่าน**ทั้งหกสถานะออกและห้ามเสนอ unpack กับสี่สถานะที่ไม่ใช่
 * `PACKED`
 */
export const PACKING_UNIT_STATUSES = [
  DRAFT_PACKING_STATUS,
  PACKING_PACKING_STATUS,
  PACKED_PACKING_STATUS,
  LOADED_PACKING_STATUS,
  DISPATCHED_PACKING_STATUS,
  RETURNED_PACKING_STATUS,
] as const;

export type PackingUnitStatus = (typeof PACKING_UNIT_STATUSES)[number];

/* -------------------------------------------------------- ข้อจำกัดความยาว -- */

/** `plant_code` ≤10 (`master_data/models.py PLANT_CODE_LENGTH`) */
export const PLANT_CODE_MAX_LENGTH = 10;
/** `so_number` ≤20 (`sales/models.py SO_NUMBER_LENGTH`) — เลขใบที่มีอยู่แล้ว */
export const SO_NUMBER_MAX_LENGTH = 20;
/** `product_code` ≤40 (`master_data/product.py PRODUCT_CODE_LENGTH`) */
export const PRODUCT_CODE_MAX_LENGTH = 40;
/** `storage_location_code` ≤20 (`master_data/models.py STORAGE_LOCATION_CODE_LENGTH`) */
export const STORAGE_LOCATION_CODE_MAX_LENGTH = 20;

/* ------------------------------------------------------------- กฎธุรกิจ --- */
/* รหัสกฎที่ `service.py` ตั้งชื่อไว้ — เรียงตามลำดับที่ `create_packing_unit`
 * ตรวจจริง แล้วตามด้วยของ `confirm`/`unpack` */

/** `403` — โรงงานที่ระบุอยู่นอก `plant_scope` ของผู้เรียก (D-004) เหมือนทุกโดเมน */
export const PLANT_OUTSIDE_SCOPE = 'PLANT_OUTSIDE_SCOPE';
/** `422` — ไม่มีใบสั่งขายเลขนี้ **ในโรงงานนี้** (ปนกับ "อยู่โรงงานอื่น" โดยตั้งใจ) */
export const SALES_ORDER_NOT_FOUND = 'SALES_ORDER_NOT_FOUND';
/** `422` — ใบสั่งขายอยู่ในสถานะที่แพ็กไม่ได้ (ต้อง CONFIRMED/PLANNED/PARTIALLY_DELIVERED) */
export const SALES_ORDER_NOT_PACKABLE = 'SALES_ORDER_NOT_PACKABLE';
/** `422` — ใบสั่งขายนี้ไม่มีเลขบรรทัดที่ระบุ */
export const SALES_ORDER_LINE_NOT_FOUND = 'SALES_ORDER_LINE_NOT_FOUND';
/** `422` — ไม่มีสินค้ารหัสนี้ในทะเบียน */
export const PRODUCT_NOT_FOUND = 'PRODUCT_NOT_FOUND';
/** `422` — สินค้าที่ระบุไม่ตรงกับสินค้าของบรรทัดใบสั่งขาย */
export const PRODUCT_MUST_MATCH_SALES_ORDER_LINE = 'PRODUCT_MUST_MATCH_SALES_ORDER_LINE';
/** `422` — สินค้านี้ไม่มี packing standard ที่ ACTIVE */
export const PACKING_STANDARD_NOT_ACTIVE = 'PACKING_STANDARD_NOT_ACTIVE';
/** `422` — ไม่มีที่เก็บรหัสนี้ในโรงงานนี้ */
export const STORAGE_LOCATION_NOT_FOUND = 'STORAGE_LOCATION_NOT_FOUND';
/** `400` — จำนวนที่กรอก (ของกล่องหรือของ lot) ≤ 0 */
export const QUANTITY_MUST_BE_POSITIVE = 'QUANTITY_MUST_BE_POSITIVE';
/** `422` — กล่องที่ไม่มี lot เลยคือกล่องที่ตามสอบไม่ได้ */
export const PACKING_UNIT_HAS_NO_LOTS = 'PACKING_UNIT_HAS_NO_LOTS';
/** `422` — lot เดียวกันถูกใส่ซ้ำในคำขอเดียว */
export const LOT_DUPLICATED_IN_PACKAGE = 'LOT_DUPLICATED_IN_PACKAGE';
/** `422` — ไม่มี lot รหัสนี้ในทะเบียน */
export const LOT_NOT_FOUND = 'LOT_NOT_FOUND';
/** `422` — lot เป็นของโรงงานอื่น */
export const LOT_PLANT_MUST_MATCH_PACKAGE = 'LOT_PLANT_MUST_MATCH_PACKAGE';
/** `422` — lot เป็นของสินค้าอื่น (กล่องผสมสินค้าไม่ได้ แม้จะผสม lot ได้) */
export const LOT_PRODUCT_MUST_MATCH_PACKAGE = 'LOT_PRODUCT_MUST_MATCH_PACKAGE';
/** `422` — lot ไม่ได้อยู่ในสถานะ RELEASED (ตรวจทั้งตอนเปิดกล่องและตอน confirm) */
export const LOT_NOT_RELEASED = 'LOT_NOT_RELEASED';
/** `422` — ของที่ชั้นเก็บไม่พอสำหรับ lot นี้ (หักของที่กล่องอื่นจับจองไปแล้วด้วย) */
export const INSUFFICIENT_STOCK_AT_SHIPPING_LOCATION =
  'INSUFFICIENT_STOCK_AT_SHIPPING_LOCATION';
/** `409` — สองกล่องแย่งเลขกล่องเดียวกัน (race ของ server, ลองใหม่ได้) */
export const PACKAGE_NO_TAKEN = 'PACKAGE_NO_TAKEN';

/**
 * `422` — **กฎที่ไม่มี constraint ฐานข้อมูลถืออยู่** (D-105 ข้อ 1): ผลรวมจำนวนของ
 * ทุก lot ต้องเท่ากับจำนวนที่ประกาศไว้ของกล่อง ตรวจตอน `confirm` เท่านั้น
 * `details` พาตัวเลขทั้งสองข้างและผลต่างมาด้วยเสมอ
 * (`package_no`, `packed_qty`, `lot_total`, `difference`, `lot_count`)
 */
export const PACKED_QUANTITY_MUST_MATCH_LOT_TOTAL = 'PACKED_QUANTITY_MUST_MATCH_LOT_TOTAL';
/** `409` — กล่องอยู่ในสถานะที่ confirm ไม่ได้ (ต้อง PACKING เท่านั้น) */
export const PACKING_UNIT_TRANSITION_NOT_ALLOWED = 'PACKING_UNIT_TRANSITION_NOT_ALLOWED';
/** `409` — ส่ง `expected_status` มาแล้วไม่ตรงกับที่เก็บจริง (หน้าจอนี้ไม่ส่งฟิลด์นี้) */
export const PACKING_UNIT_STATUS_CHANGED = 'PACKING_UNIT_STATUS_CHANGED';
/**
 * `422` — กล่องที่ `RETURNED` (ส่งไปลูกค้าแล้วถูกตีกลับ) เปิดใหม่ไม่ได้ใน Phase นี้
 * `details.reason` เป็น `QUALITY_OF_RETURNED_GOODS_IS_UNDECIDED` เสมอ
 */
export const RETURNED_PACKAGE_CANNOT_BE_UNPACKED = 'RETURNED_PACKAGE_CANNOT_BE_UNPACKED';
/** `422` — กล่องอยู่ในสถานะที่ unpack ไม่ได้ (ต้อง PACKED เท่านั้น — DRAFT/PACKING
 * เปิดอยู่แล้ว, LOADED ต้องลงจากรถก่อน, DISPATCHED ต้องกลับด้วย Delivery reversal) */
export const PACKING_UNIT_CANNOT_BE_UNPACKED = 'PACKING_UNIT_CANNOT_BE_UNPACKED';
/** `400` — ฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของ (เลขกล่อง/สถานะ/ pack record) ถูกส่งมาในคำขอ */
export const PACKING_UNIT_FIELD_IS_SERVER_OWNED = 'PACKING_UNIT_FIELD_IS_SERVER_OWNED';

/* ------------------------------------------------------------- resource --- */

/** หนึ่ง lot ในกล่อง — `packed_qty` เป็น **สตริงเสมอ** (`NUMERIC(18,4)`) */
export interface PackingUnitLot {
  readonly lot_id: string;
  readonly packed_qty: string;
}

/**
 * กล่องหนึ่งใบ **พร้อม lot ครบ** — รูปของ `GET /{id}`, `POST`, `confirm`, `unpack`
 *
 * ทุกฟิลด์ตรงตาม `PackingUnitResponse` ใน `schemas.py` ตรง ๆ — สี่ฟิลด์
 * "pack record"/"unpack record" เป็น `string | null` เพราะประกาศ
 * `Optional[...] = None` ส่วนที่เหลือ **required เสมอ**
 * (`ck_packing_unit_pack_record_is_whole` คือสิ่งที่ทำให้สี่ฟิลด์นั้นเป็นคู่กัน
 * เสมอ ไม่ใช่ครบสามในสี่)
 */
export interface PackingUnit {
  readonly packing_unit_id: string;
  readonly package_no: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly so_id: string;
  readonly so_number: string;
  readonly line_no: number;
  readonly product_id: string;
  readonly product_code: string;
  readonly packing_standard_id: string;
  readonly storage_location_id: string;
  readonly storage_location_code: string;
  /** ทศนิยมที่เดินทางมาเป็นสตริงเสมอ — ห้ามแปลงเป็น number */
  readonly packed_qty: string;
  readonly uom_code: string;
  readonly status: string;
  readonly created_by_employee_id: string;
  readonly packed_by_employee_id: string | null;
  readonly packed_at: string | null;
  readonly unpacked_by_employee_id: string | null;
  readonly unpacked_at: string | null;
  readonly lots: readonly PackingUnitLot[];
  readonly created_at: string;
  readonly updated_at: string;
}

/** กล่องหนึ่งใบ **ไม่มี lot** — รูปของแต่ละ item ใน `GET /v1/packing-units` */
export type PackingUnitSummary = Omit<PackingUnit, 'lots'>;

/** `GET /v1/packing-units` — envelope มาตรฐานของทุกกลุ่ม */
export interface PackingUnitListPage {
  readonly items: readonly PackingUnitSummary[];
  readonly next_cursor: string | null;
}

/* -------------------------------------------------------------- requests -- */

/** หนึ่ง lot ที่ส่งไปตอนสร้างกล่อง — สองคีย์ตามที่ `PackingUnitLotRequest` รับ */
export interface CreatePackingUnitLot {
  readonly lot_id: string;
  /** สตริงเสมอ — `> 0` */
  readonly packed_qty: string;
}

/**
 * body ของ `POST /v1/packing-units` — **หัวกล่องและ lot ทั้งหมดในคำขอเดียว**
 *
 * ไม่มีคีย์ที่เซิร์ฟเวอร์เป็นเจ้าของเลย (`package_no`, `packing_standard_id`,
 * `status`, ผู้กระทำ, เวลา — ดู `PACKING_SERVER_OWNED_FIELDS`)
 */
export interface CreatePackingUnitBody {
  readonly plant_code: string;
  readonly so_number: string;
  readonly line_no: number;
  readonly product_code: string;
  readonly storage_location_code: string;
  /** สตริงเสมอ — จำนวนรวมของกล่อง `> 0` */
  readonly packed_qty: string;
  readonly lots: readonly CreatePackingUnitLot[];
}
